import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ShieldCheck, TrendingUp, Users, FileText, Zap, BarChart3, Check } from 'lucide-react';

const BecomeBroker = () => {
  const features = [
    { icon: FileText, title: 'Auto Landing Pages', desc: 'Get unique SEO-friendly URLs for every project you list.' },
    { icon: Users, title: 'Lead CRM', desc: 'Manage all buyer enquiries with our built-in CRM & pipeline.' },
    { icon: BarChart3, title: 'Analytics', desc: 'Track leads, conversions, and performance in real-time.' },
    { icon: ShieldCheck, title: 'RERA Verified Profile', desc: 'Public verified profile that builds buyer trust instantly.' },
    { icon: Zap, title: 'Instant Publishing', desc: 'Add projects with photos, brochures, and floor plans.' },
    { icon: TrendingUp, title: 'Grow Faster', desc: 'Access thousands of buyers across Pan-India.' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <section className="relative bg-[#0F2C59] overflow-hidden">
        <img src="https://images.pexels.com/photos/2408230/pexels-photo-2408230.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs font-semibold mb-5">
              <ShieldCheck size={14} className="text-[#F97316]" /> For Real Estate Brokers
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight">
              Power Your Real Estate<br />Business with <span className="text-[#F97316]">PropertyByKilo</span>
            </h1>
            <p className="mt-4 text-lg text-slate-200">
              Join 5,000+ brokers from Tier-1, Tier-2 & Tier-3 cities. Get your own dashboard, landing pages, and lead engine — all in one platform.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/register?role=broker">
                <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] font-bold" data-testid="register-broker-cta">Register as Broker</Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-white/30 text-white bg-white/10 hover:bg-white/20" data-testid="broker-login-cta">Broker Login</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#F97316]">Everything you need</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0F2C59] mt-2">Built for Modern Brokers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200 p-6" data-testid={`broker-feature-${i}`}>
              <div className="w-12 h-12 rounded-md bg-orange-50 flex items-center justify-center mb-4">
                <f.icon size={22} className="text-[#F97316]" />
              </div>
              <h3 className="font-bold text-lg text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-3xl text-[#0F2C59] text-center mb-10">How It Works</h2>
          <div className="space-y-4">
            {[
              { step: '01', title: 'Sign Up & Complete KYC', desc: 'Register and complete your professional profile with RERA details.' },
              { step: '02', title: 'Get Verified by Admin', desc: 'Our team reviews your KYC and approves within 24-48 hours.' },
              { step: '03', title: 'Add Projects & Properties', desc: 'Upload builder projects with photos, floor plans, and brochures.' },
              { step: '04', title: 'Generate Landing Pages', desc: 'Auto-create unique SEO URLs for each project you list.' },
              { step: '05', title: 'Capture & Convert Leads', desc: 'Use the CRM to manage buyer enquiries and close deals.' }
            ].map((s, i) => (
              <div key={i} className="flex gap-4 items-start p-5 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 rounded-md bg-[#0F2C59] text-white flex items-center justify-center font-black">{s.step}</div>
                <div>
                  <h3 className="font-bold text-slate-900">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/register?role=broker">
              <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] font-bold" data-testid="bottom-register-cta">Get Started — It's Free</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomeBroker;
